/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { EveQuickOrderTabInput, IEveQuickOrderTabData } from '../../actions/eve-quick-order-tab.action';
import { IEveQuickOrderViewProps } from './eve-quick-order';

type Tab = IEveQuickOrderTabData['currentTab'];

const usePrev = <T,>(state: T) => {
    const prevState = React.useRef<T | undefined>();
    React.useEffect(() => void (prevState.current = state), [state]);
    return prevState.current;
};

const quickOrderView: React.FC<IEveQuickOrderViewProps> = props => {
    const baseProps = { 'data-ignore-parent-css': true };
    const { context, quickOrderList, orderUpload, data, rfq, config } = props;
    const { actionContext } = context;
    const { transitionDisabled } = config;
    const { quickOrderTab } = data;
    const { currentTab } = quickOrderTab.result ?? {};
    const tabLabels: Tab[] = ['manual', 'import', 'rfq'];
    const cn = (suffix: string) => `cps-quick-order${suffix}`;

    const prevTab = usePrev(currentTab);

    return (
        <>
            <div {...baseProps} className={cn('__tab-container')}>
                {tabLabels.map(label => (
                    <button
                        {...baseProps}
                        className={label === quickOrderTab.result?.currentTab ? cn(`__tab--current`) : ''}
                        key={label}
                        onClick={() => actionContext.update<IEveQuickOrderTabData>(new EveQuickOrderTabInput(), { currentTab: label })}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <div {...baseProps} className={cn('__tab-body-container')}>
                <CSSTransition timeout={transitionDisabled ? 0 : 300} in={currentTab === 'manual'} unmountOnExit>
                    <>{quickOrderList}</>
                </CSSTransition>
                <CSSTransition
                    timeout={transitionDisabled ? 0 : 300}
                    in={currentTab === 'import'}
                    unmountOnExit
                    classNames={{
                        enterActive: `enter-active-from-${prevTab === 'manual' ? 'right' : 'left'}`,
                        exitActive: `exit-active-to-${currentTab === 'manual' ? 'right' : 'left'}`
                    }}
                >
                    <>{orderUpload}</>
                </CSSTransition>
                <CSSTransition timeout={transitionDisabled ? 0 : 300} in={currentTab === 'rfq'} unmountOnExit>
                    <>{rfq}</>
                </CSSTransition>
            </div>
        </>
    );
};

export default quickOrderView;
